import {ApiMethods, ApiServiceController, IApiExecutor} from "../../../../../@bizkeytech";
import ApiEndpoints from "../../endpoints";
import * as models from "./index.types";

/**@inheritDoc*/
export class EmployeeProfilesController extends ApiServiceController {

    /**@inheritDoc*/
    public constructor(executor: IApiExecutor | undefined) {
        super(executor);
    }

    /**
     * Lists the organization groups that are available.
     *
     * @param abortController   The abort controller that will be used to cancel the request.
     */
    public async list(abortController?: AbortController): models.ListEmployeeProfilesResponse {
        return this.executor?.execute({
            method: ApiMethods.get,
            url: ApiEndpoints.employeeProfiles.list,
            signal: abortController?.signal,
        });
    }

    /**
     * Fetches the current employee profile from the server.
     *
     * @param token             The token of the operator.
     * @param abortController   The abort controller that will be used to cancel the request.
     */
    public async getCurrentProfile(token: string, abortController?: AbortController): models.GetCurrentEmployeeProfileResponse {
        return this.executor?.execute({
            method: ApiMethods.get,
            url: ApiEndpoints.employeeProfiles.getCurrentProfile,
            signal: abortController?.signal,
            headers: {
                Authorization: token,
            },
        });
    }

    /**
     * Fetches the employee profile from the server.
     *
     * @param request           The request to fetch the employee profile.
     * @param abortController   The abort controller that will be used to cancel the request.
     */
    public async get(request: models.GetEmployeeProfileRequest, abortController?: AbortController): models.GetEmployeeProfileResponse {
        return this.executor?.execute({
            method: ApiMethods.get,
            url: ApiEndpoints.employeeProfiles.getEmployeeProfile.concat(`/${request.id}`),
            signal: abortController?.signal,
        });
    }

    /**
     * Activates the employee profile.
     *
     * @param request           The request to activate the employee profile.
     * @param abortController   The abort controller that will be used to cancel the request.
     */
    public async activate(request: models.ActivateEmployeeProfileRequest, abortController?: AbortController): models.ActivateEmployeeProfileResponse {
        return this.executor?.execute({
            method: ApiMethods.put,
            url: ApiEndpoints.employeeProfiles.activateEmployeeProfile,
            signal: abortController?.signal,
            data: request,
        });
    }

    /**
     * Deactivates the employee profile.
     *
     * @param request           The request to deactivate the employee profile.
     * @param abortController   The abort controller that will be used to cancel the request.
     */
    public async deactivate(request: models.DeactivateEmployeeProfileRequest, abortController?: AbortController): models.DeactivateEmployeeProfileResponse {
        return this.executor?.execute({
            method: ApiMethods.put,
            url: ApiEndpoints.employeeProfiles.deactivateEmployeeProfile,
            signal: abortController?.signal,
            data: request,
        });
    }

    /**
     * Updates the employee profile.
     *
     * @param request           The request to update the employee profile.
     * @param abortController   The abort controller that will be used to cancel the request.
     */
    public async update(request: models.UpdateEmployeeProfileRequest, abortController?: AbortController): models.UpdateEmployeeProfileResponse {
        return this.executor?.execute({
            method: ApiMethods.put,
            url: ApiEndpoints.employeeProfiles.updateEmployeeProfile,
            signal: abortController?.signal,
            data: request,
        });
    }

    /**
     * Searches the employee profiles.
     *
     * @param request           The request to search the employee profiles.
     * @param abortController   The abort controller that will be used to cancel the request.
     */
    public async search(request: models.SearchEmployeeProfilesRequest, abortController?: AbortController): models.SearchEmployeeProfilesResponse {
        return this.executor?.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.employeeProfiles.searchEmployeeProfiles,
            signal: abortController?.signal,
            data: request,
        });
    }

    /**
     * Fetches the employee profile history from the server.
     *
     * @param request           The request to fetch the employee profile property history.
     * @param abortController   The abort controller that will be used to cancel the request.
     */
    public async getProfileHistory(request: models.GetEmployeeProfileHistoryRequest, abortController?: AbortController): models.GetEmployeeProfileHistoryResponse {
        return this.executor?.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.employeeProfiles.getEmployeeProfileHistory,
            signal: abortController?.signal,
            data: request,
        });
    }

    /**
     * Fetches the employee profile property history from the server.
     *
     * @param request           The request to fetch the employee profile property history.
     * @param abortController   The abort controller that will be used to cancel the request.
     */
    public async getProfilePropertyHistory(request: models.GetEmployeeProfilePropertyHistoryRequest, abortController?: AbortController): models.GetEmployeeProfilePropertyHistoryResponse {
        return this.executor?.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.employeeProfiles.getEmployeeProfilePropertyHistory,
            signal: abortController?.signal,
            data: request,
        });
    }
}


export * from './index.types';
export default EmployeeProfilesController;